// Product Selector V1: size cards, savings progress, flavor steppers, total and add to cart.
// Relies on window.kiidAdapter (kiid-adapter.js) for money formatting and cart requests.

if (!customElements.get('kiid-product-selector')) {
  customElements.define(
    'kiid-product-selector',
    class ProductSelector extends HTMLElement {
      connectedCallback() {
        this.tiersBySize = {
          12: JSON.parse(this.dataset.tiers12 || '[]'),
          30: JSON.parse(this.dataset.tiers30 || '[]'),
        };
        this.bubblesBySize = {
          12: (this.dataset.bubble12 || '').split('|'),
          30: (this.dataset.bubble30 || '').split('|'),
        };
        this.underBySize = {
          12: (this.dataset.under12 || '').split('|'),
          30: (this.dataset.under30 || '').split('|'),
        };
        this.flavorCount = Number(this.dataset.flavorCount) || 3;
        this.moneyFormat = this.dataset.moneyFormat;

        this.rows = [...this.querySelectorAll('.kps__flavor')];
        this.cups = [...this.querySelectorAll('.kps__pick')];
        this.dialog = this.querySelector('.kps__dialog');
        this.dialogPanels = [...this.querySelectorAll('.kps__dialog-panel')];
        this.cupsSection = this.querySelector('[data-cups]');
        this.progress = this.querySelector('[data-progress]');
        this.tiers = [...this.querySelectorAll('.kps__progress .kps__tiers:first-child .kps__tier')];
        this.priceTiers = [...this.querySelectorAll('.kps__progress .kps__tiers:last-child .kps__tier')];
        this.fill = this.querySelector('.kps__track-fill');
        this.dots = [...this.querySelectorAll('.kps__dot')];
        this.cta = this.querySelector('[data-cta]');
        this.error = this.querySelector('.kps__error');
        this.totalEl = this.querySelector('[data-total]');
        this.origEl = this.querySelector('.kps__summary-orig');
        this.headingEl = this.querySelector('[data-flavors-heading]');
        this.noteEl = this.querySelector('[data-flavors-note]');

        // null means no size has been chosen yet (default size "none"): flavors are shown but locked until a size is picked.
        this.size = this.querySelector('.kps__size input:checked')?.value || (this.dataset.defaultSize === 'none' ? null : this.dataset.defaultSize || '12');
        this.qty = new Map(); // flavor handle -> bags (standard mode)
        this.picks = new Set(); // flavor handles (Starter Pack)
        this.cup = null;

        this.querySelectorAll('.kps__size input').forEach((input) => {
          input.addEventListener('change', () => {
            if (input.checked) this.setSize(input.value);
          });
        });
        this.addEventListener('click', (event) => this.onClick(event));
        if (this.dialog) {
          this.querySelector('.kps__dialog-close').addEventListener('click', () => this.dialog.close());
          // A click on the dialog element itself (not its content) is a click on the backdrop.
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });
          this.dialog.addEventListener('close', () => {
            this.dialog.querySelectorAll('video').forEach((video) => video.pause());
            this.lastViewButton?.focus();
          });
        }
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.cta.addEventListener('click', () => this.onCta());

        this.render();
      }

      get isStarter() {
        return this.size === 'starter';
      }

      get hasSize() {
        return this.size !== null;
      }

      // Which variant column the flavor rows use: the Starter Pack is 12-serving bags.
      get sizeKey() {
        return this.isStarter ? '12' : this.size;
      }

      setSize(size) {
        this.size = size;
        this.error.hidden = true;
        this.render();
      }

      money(cents) {
        return window.kiidAdapter.formatMoney(Math.round(cents), this.moneyFormat);
      }

      rowHas(row) {
        // No size chosen yet: show every flavor that exists in at least one size.
        if (!this.hasSize) return Boolean(row.dataset.variant12 || row.dataset.variant30);
        // Starter Pack flavors must have a bundle label, or they can't be sent to the bundle.
        if (this.isStarter && !row.dataset.bundle) return false;
        return Boolean(row.dataset[`variant${this.sizeKey}`]);
      }

      rowOut(row) {
        if (!this.hasSize) return false;
        return row.dataset[`stock${this.sizeKey}`] === 'out';
      }

      rowPrice(row) {
        return Number(row.dataset[`price${this.sizeKey}`]);
      }

      // Full price of a bag: the compare-at price when the variant has one, otherwise its price.
      rowFullPrice(row) {
        const compare = Number(row.dataset[`compare${this.sizeKey}`]) || 0;
        return Math.max(compare, this.rowPrice(row));
      }

      // Opens the larger picture of one cup (colors and feature callouts) in a modal dialog.
      openCup(button) {
        const panel = this.dialogPanels.find((item) => item.dataset.cup === button.dataset.viewCup);
        if (!panel || !this.dialog) return;
        this.dialogPanels.forEach((item) => {
          item.hidden = item !== panel;
        });
        this.querySelector('.kps__dialog-title').textContent = panel.dataset.title;
        this.lastViewButton = button;
        this.dialog.showModal();
        this.dialog.focus();
      }

      onClick(event) {
        const view = event.target.closest('.kps__view');
        if (view) {
          this.openCup(view);
          return;
        }

        const step = event.target.closest('.kps__step');
        if (step) {
          const row = step.closest('.kps__flavor');
          const next = this.bagsFor(row) + Number(step.dataset.step);
          this.qty.set(row.dataset.handle, Math.max(0, Math.min(99, next)));
          this.render();
          return;
        }

        const cup = event.target.closest('.kps__pick');
        if (cup && !cup.disabled) {
          this.cup = cup.dataset.variant;
          this.render();
          return;
        }

        const row = event.target.closest('.kps__flavor');
        if (row && this.isStarter && !this.rowOut(row)) this.togglePick(row);
      }

      onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const row = event.target.closest('.kps__flavor');
        if (row && event.target === row && this.isStarter && !this.rowOut(row)) {
          event.preventDefault();
          this.togglePick(row);
        }
      }

      // Exactly flavorCount flavors are chosen: once that many are picked, others can't be added
      // until one is deselected.
      togglePick(row) {
        const { handle } = row.dataset;
        if (this.picks.has(handle)) {
          this.picks.delete(handle);
        } else {
          if (this.startersPicked().length >= this.flavorCount) return;
          this.picks.add(handle);
        }
        this.render();
      }

      // Bags for a row at the current size. A flavor that is sold out at this size counts as none,
      // even if bags were added before switching sizes, so it can't block the add to cart.
      bagsFor(row) {
        return this.rowOut(row) ? 0 : this.qty.get(row.dataset.handle) || 0;
      }

      visibleRows() {
        return this.rows.filter((row) => this.rowHas(row));
      }

      totalBags() {
        return this.visibleRows().reduce((sum, row) => sum + this.bagsFor(row), 0);
      }

      // Percent for a quantity: the highest tier whose quantity has been reached.
      discountFor(quantity) {
        const tiers = this.tiersBySize[this.size] || [];
        return tiers.reduce((best, tier) => (quantity >= tier.quantity ? Math.max(best, tier.percent) : best), 0);
      }

      render() {
        const starter = this.isStarter;
        this.dataset.mode = starter ? 'starter' : this.hasSize ? 'standard' : 'nosize';
        this.progress.hidden = starter || !this.hasSize;
        if (this.cupsSection) this.cupsSection.hidden = !starter;

        this.renderRows();
        if (starter) this.renderStarterHeading();
        else this.renderStandardHeading();
        if (!starter && this.hasSize) this.renderProgress();
        this.cups.forEach((cup) => {
          cup.closest('.kps__cup').hidden = !cup.dataset.bundle;
          cup.setAttribute('aria-checked', String(cup.dataset.variant === this.cup));
        });
        this.renderSummary();
      }

      renderRows() {
        const starter = this.isStarter;
        const atLimit = starter && this.startersPicked().length >= this.flavorCount;
        this.rows.forEach((row) => {
          const has = this.rowHas(row);
          const out = this.rowOut(row);
          const qty = this.bagsFor(row);
          const picked = this.picks.has(row.dataset.handle);
          row.hidden = !has;
          row.classList.toggle('kps__flavor--out', out);
          row.querySelector('.kps__flavor-stock').hidden = !out;
          row.classList.toggle('kps__flavor--on', starter ? picked && !out : qty > 0);
          row.querySelector('.kps__qty').textContent = qty;
          const [dec, inc] = row.querySelectorAll('.kps__step');
          dec.disabled = qty === 0 || !this.hasSize;
          inc.disabled = out || !this.hasSize;

          const full = atLimit && !picked && !out;
          row.classList.toggle('kps__flavor--full', full);

          if (starter) {
            row.setAttribute('role', 'button');
            row.setAttribute('aria-pressed', String(picked));
            row.setAttribute('aria-disabled', String(out || full));
            row.tabIndex = out || full ? -1 : 0;
          } else {
            row.removeAttribute('role');
            row.removeAttribute('aria-pressed');
            row.removeAttribute('aria-disabled');
            row.removeAttribute('tabindex');
          }
        });
      }

      renderStandardHeading() {
        if (!this.hasSize) {
          this.noteEl.textContent = this.dataset.noSizeNote || '';
          return;
        }
        const total = this.totalBags();
        const unit = total === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
        this.noteEl.textContent = `${total} ${unit}`;
      }

      renderStarterHeading() {
        const visiblePicks = this.visibleRows().filter((row) => this.picks.has(row.dataset.handle)).length;
        this.noteEl.textContent = `Choose ${this.flavorCount} · ${visiblePicks} selected`;
      }

      // Weighted average of the chosen bags' prices, or the first visible flavor's price with none.
      averagePrice() {
        const rows = this.visibleRows();
        const bags = rows.reduce((sum, row) => sum + this.bagsFor(row), 0);
        if (bags > 0) {
          const value = rows.reduce((sum, row) => sum + this.rowPrice(row) * this.bagsFor(row), 0);
          return value / bags;
        }
        return rows.length ? this.rowPrice(rows[0]) : 0;
      }

      renderProgress() {
        const total = this.totalBags();
        const tiers = this.tiersBySize[this.size] || [];
        const bubbles = this.bubblesBySize[this.size] || [];
        const average = this.averagePrice();
        const suffix = this.dataset.perBagSuffix;
        const unders = this.underBySize[this.size] || [];

        let reached = 0;
        tiers.forEach((tier, index) => {
          const isReached = total >= tier.quantity;
          if (isReached) reached = index + 1;
          // The first tier is lit from the start, before any bag is added; its check mark (the
          // "reached" state) waits for a real bag.
          const isLit = index === 0 || isReached;
          const top = this.tiers[index];
          const bottom = this.priceTiers[index];
          if (!top || !bottom) return;

          const bubble = top.querySelector('.kps__bubble');
          const text = (bubbles[index] || '').trim();
          // Without text the bubble keeps its space (invisible) so the tier labels stay level.
          bubble.hidden = false;
          bubble.textContent = text || ' ';
          bubble.classList.toggle('kps__bubble--empty', !text);
          bubble.setAttribute('aria-hidden', String(!text));

          const price = average * (1 - tier.percent / 100);
          bottom.querySelector('.kps__price').innerHTML = average
            ? `<strong>${this.money(price)}</strong>${suffix}`
            : '';
          // Typed in by hand in the block settings, not calculated.
          bottom.querySelector('.kps__off').textContent = (unders[index] || '').trim();

          top.classList.toggle('kps__tier--reached', isReached);
          bottom.classList.toggle('kps__tier--reached', isReached);
          top.classList.toggle('kps__tier--lit', isLit);
          // Once a higher tier is reached, the lower tiers' bubbles are grayed out so only the
          // discount the shopper will actually get stands out. Tiers above stay untouched.
          top.classList.toggle('kps__tier--superseded', total >= (tiers[index + 1]?.quantity ?? Infinity));
          bottom.classList.toggle('kps__tier--lit', isLit);
        });

        // The bar moves toward the next tier as bags are added (proportionally to the quantity
        // needed), but a tier's dot only lights up once its quantity is reached. The first dot is
        // lit from the start.
        const second = tiers[1]?.quantity || 2;
        const third = tiers[2]?.quantity || second + 1;
        let percent;
        if (total <= second) percent = (50 * total) / second;
        else percent = 50 + (50 * (total - second)) / Math.max(third - second, 1);
        this.fill.style.width = `${Math.min(100, percent)}%`;

        const lit = Math.max(reached, 1);
        this.dots.forEach((dot, index) => {
          dot.classList.toggle('kps__dot--reached', lit >= index + 1);
          dot.classList.toggle('kps__dot--current', lit === index + 1);
        });
      }

      startersPicked() {
        return this.visibleRows().filter((row) => this.picks.has(row.dataset.handle) && !this.rowOut(row));
      }

      renderSummary() {
        let label = this.dataset.buttonLabel;
        let enabled = true;
        this.origEl.hidden = true;

        if (!this.hasSize) {
          this.totalEl.textContent = this.money(0);
          this.cta.textContent = this.dataset.buttonNoSizeLabel || label;
          this.cta.setAttribute('aria-disabled', 'true');
          return;
        }

        if (this.isStarter) {
          const price = Number(this.dataset.starterPrice) || 0;
          const compare = Number(this.dataset.starterCompare) || 0;
          this.totalEl.textContent = this.money(price);
          if (compare > price) {
            this.origEl.textContent = this.money(compare);
            this.origEl.hidden = false;
          }

          const picked = this.startersPicked().length;
          if (picked < this.flavorCount) {
            enabled = false;
            const missing = this.flavorCount - picked;
            const template = missing === 1 ? this.dataset.buttonFlavorsOne : this.dataset.buttonFlavorsOther;
            label = template.replace('{count}', missing);
          } else if (!this.cup) {
            enabled = false;
            label = this.dataset.buttonCupsLabel;
          }
        } else {
          const rows = this.visibleRows();
          const bags = this.totalBags();
          const price = rows.reduce((sum, row) => sum + this.rowPrice(row) * this.bagsFor(row), 0);
          const full = rows.reduce((sum, row) => sum + this.rowFullPrice(row) * this.bagsFor(row), 0);
          const total = price * (1 - this.discountFor(bags) / 100);
          this.totalEl.textContent = this.money(total);
          if (Math.round(full) > Math.round(total)) {
            this.origEl.textContent = this.money(full);
            this.origEl.hidden = false;
          }
          if (!bags) {
            enabled = false;
            label = this.dataset.buttonEmptyLabel;
          }
        }

        this.cta.textContent = label;
        this.cta.setAttribute('aria-disabled', String(!enabled));
      }

      onCta() {
        if (this.cta.getAttribute('aria-disabled') === 'true' || this.cta.getAttribute('aria-busy') === 'true') return;
        this.error.hidden = true;
        if (this.isStarter) this.addStarter();
        else this.addToCart();
      }

      async addToCart() {
        const items = this.visibleRows()
          .map((row) => ({ id: Number(row.dataset[`variant${this.size}`]), quantity: this.bagsFor(row) }))
          .filter((item) => item.quantity > 0);
        if (!items.length) return;

        this.cta.setAttribute('aria-busy', 'true');
        this.cta.setAttribute('aria-disabled', 'true');
        try {
          await window.kiidAdapter.addItems(items, { source: 'kiid-product-selector' });
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.cta.removeAttribute('aria-busy');
          this.renderSummary();
        }
      }

      // The Starter Pack is one Simple Bundles "Infinite Options" bundle line. Each option is a line
      // item property named after the bundle option; _bundle_selection lists every chosen value in
      // option order, joined by " <> ". Values are the bundle's own labels (custom.bundle_option_value),
      // and the option names come from the block settings, so they must match the bundle exactly.
      async addStarter() {
        const picked = this.startersPicked();
        const cup = this.cups.find((item) => item.dataset.variant === this.cup && !item.closest('.kps__cup').hidden);
        const variantId = Number(this.dataset.starterVariant);
        const flavorName = this.dataset.starterFlavorOption || 'Flavor {n}';
        const cupName = this.dataset.starterCupOption || 'Cup';
        const fixed = (this.dataset.starterFixed || '')
          .split(';')
          .map((pair) => pair.split('='))
          .filter(([key, value]) => key?.trim() && value?.trim())
          .map(([key, value]) => [key.trim(), value.trim()]);

        if (!variantId || picked.length !== this.flavorCount || !cup) {
          this.showError('Please choose your flavors and cup.');
          return;
        }

        const entries = picked.map((row, index) => [flavorName.replace('{n}', index + 1), row.dataset.bundle]);
        entries.push([cupName, cup.dataset.bundle], ...fixed);
        const properties = Object.fromEntries(entries);
        properties._bundle_selection = entries.map(([, value]) => value).join(' <> ');

        this.cta.setAttribute('aria-busy', 'true');
        this.cta.setAttribute('aria-disabled', 'true');
        try {
          await window.kiidAdapter.addItems([{ id: variantId, quantity: 1, properties }], { source: 'kiid-product-selector' });
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.cta.removeAttribute('aria-busy');
          this.renderSummary();
        }
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = false;
      }
    }
  );
}
